import { createElement } from 'react';
import { BotIcon, WorkflowIcon } from 'lucide-react';

import { defineModule } from '@/shared/registry/types';

import { useBizprocCommands, useBizprocNavCounter } from './components/extensionHooks';
import {
  LazyAutomationSection,
  LazyBulkStartProcess,
  LazyDocumentProcessesTab,
  LazyDocumentRobotsTab,
  LazyInstancePanel,
  LazyProcessesSettingsTab,
  LazyTaskPanel,
} from './lazy';
import { registerBizprocRealtime } from './realtime';

const page = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({
  Component: (await load()).default,
});

/**
 * `bizproc` module — business processes (docs/modules/bizproc.md §13): «My tasks» (`/bizproc/tasks`), the running
 * instances and their pages, the processes with their start rules (the visual designer comes next), incidents and
 * settings; the task panel `bizproc-task:<id>` (the link of the notifications) and the instance panel
 * `bizproc-instance:<id>`; the «Processes» tab of documents' cards (`documentTabs`) and the «Business processes» tab of
 * a list's settings (`documentSettingsTabs`); the sidebar counter of waiting tasks and ⌘K entries. For the CRM: the «Роботы»
 * section of its page (`pageSections`, `/crm/automation`: the robots and triggers of a pipeline, for administrators) and
 * the «Роботы» tab of deals' and leads' cards (what the stage's robots did for the document). For the tasks: the page of a
 * project's board (`/bizproc/automation/tasks.task?category=<project>`, the link «Роботы» of the tasks module) and the same tab
 * on the task card.
 */
export const module = defineModule({
  id: 'bizproc',
  routes: [
    {
      path: 'bizproc',
      children: [
        { index: true, lazy: page(() => import('./pages/RedirectToTasks')) },
        { path: 'tasks', lazy: page(() => import('./pages/TasksPage')) },
        { path: 'instances', lazy: page(() => import('./pages/InstancesPage')) },
        { path: 'instances/:id', lazy: page(() => import('./pages/InstancePage')) },
        { path: 'definitions', lazy: page(() => import('./pages/DefinitionsPage')) },
        {
          path: 'definitions/:id',
          // The designer fills the content card: no page padding around the canvas.
          handle: { layout: 'full' },
          lazy: page(() => import('./pages/DefinitionPage')),
        },
        { path: 'templates', lazy: page(() => import('./pages/TemplatesPage')) },
        { path: 'schedules', lazy: page(() => import('./pages/SchedulesPage')) },
        { path: 'incidents', lazy: page(() => import('./pages/IncidentsPage')) },
        { path: 'automation/:documentType', lazy: page(() => import('./pages/AutomationPage')) },
        { path: 'webhooks', lazy: page(() => import('./pages/WebhooksPage')) },
        { path: 'settings', lazy: page(() => import('./pages/SettingsPage')) },
        { path: '*', lazy: page(() => import('./pages/RedirectToTasks')) },
      ],
    },
  ],
  nav: [
    {
      id: 'bizproc',
      labelKey: 'bizproc:nav.title',
      icon: WorkflowIcon,
      to: '/bizproc',
      order: 37,
      section: 'main',
      useCounter: useBizprocNavCounter,
    },
  ],
  sidePanels: [
    { type: 'bizproc-task', component: LazyTaskPanel, size: 'lg', titleKey: 'bizproc:task.panelTitle' },
    {
      type: 'bizproc-instance',
      component: LazyInstancePanel,
      size: 'lg',
      titleKey: 'bizproc:instance.panelTitle',
    },
  ],
  documentTabs: [
    {
      id: 'bizproc',
      labelKey: 'bizproc:documentTab.title',
      order: 90,
      documentTypes: ['*'],
      element: (document) =>
        createElement(LazyDocumentProcessesTab, { type: document.type, id: document.id }),
    },
    {
      id: 'robots',
      labelKey: 'bizproc:robotsTab.title',
      order: 91,
      documentTypes: ['crm.deal', 'crm.lead', 'crm.item:*', 'tasks.task'],
      element: (document) => createElement(LazyDocumentRobotsTab, { type: document.type, id: document.id }),
    },
  ],
  pageSections: [
    {
      page: 'crm',
      id: 'automation',
      labelKey: 'bizproc:automation.section',
      icon: BotIcon,
      order: 20,
      adminOnly: true,
      tool: true,
      element: () => createElement(LazyAutomationSection),
    },
    {
      // The robots of the dialogs of a line, for the supervisors of the contact center (docs/modules/bizproc.md §13.12).
      page: 'contact-center',
      id: 'robots',
      labelKey: 'bizproc:automation.section',
      icon: BotIcon,
      order: 20,
      element: () => createElement(LazyAutomationSection, { module: 'openlines', categoriesSource: 'openlines.lines' }),
    },
  ],
  documentBulkActions: [
    {
      id: 'start-process',
      order: 90,
      // Documents of a type with business processes (the CRM leaves the action out where the type has them off).
      documentTypes: ['crm.item:*'],
      element: ({ type, ids, onDone }) => createElement(LazyBulkStartProcess, { type, ids, onDone }),
    },
  ],
  documentSettingsTabs: [
    {
      id: 'bizproc',
      labelKey: 'bizproc:settingsTab.title',
      order: 50,
      documentTypes: ['lists.item:*'],
      element: ({ type }) => createElement(LazyProcessesSettingsTab, { type }),
    },
  ],
  commands: [
    { id: 'bizproc', headingKey: 'bizproc:commands.heading', order: 37, useItems: useBizprocCommands },
  ],
  setup: ({ realtime, queryClient }) => {
    registerBizprocRealtime(realtime, queryClient);
  },
});
